import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { logout } from "../utils/auth";
import logo2 from "../assets/img/logo2.png";
import background from "../assets/img/background.png";

export default function ShiftConfirm() {
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [error, setError] = useState("");
    const [showRejectForm, setShowRejectForm] = useState(false);
    const [rejectNote, setRejectNote] = useState("");

    const [data, setData] = useState(null);

    useEffect(() => {
        fetchStatus();
    }, []);

    function fetchStatus() {
        setLoading(true);
        window.axios
            .get("/shift-status")
            .then((response) => {
                const d = response.data;
                if (!d.needs_confirmation) {
                    // Nema potvrde - vrati na glavnu
                    navigate("/");
                    return;
                }
                setData(d);
            })
            .catch(() => {
                navigate("/");
            })
            .finally(() => {
                setLoading(false);
            });
    }

    function handleConfirm() {
        setActionLoading(true);
        setError("");

        window.axios
            .post("/shift-confirm")
            .then(() => {
                navigate("/");
            })
            .catch((err) => {
                setError(err.response?.data?.message || "Greška pri potvrdi smjene.");
            })
            .finally(() => {
                setActionLoading(false);
            });
    }

    function handleReject() {
        if (rejectNote.trim().length < 3) {
            setError("Razlog mora imati najmanje 3 znaka.");
            return;
        }

        setActionLoading(true);
        setError("");

        window.axios
            .post("/shift-reject", { note: rejectNote })
            .then(() => {
                navigate("/");
            })
            .catch((err) => {
                setError(err.response?.data?.message || "Greška pri odbijanju smjene.");
            })
            .finally(() => {
                setActionLoading(false);
            });
    }

    function handleLogout() {
        logout(navigate);
    }

    if (loading) {
        return (
            <div className="fixed w-full h-screen left-0 top-0 z-10 flex items-center justify-center bg-cover" style={{ backgroundImage: `url(${background})` }}>
                <div className="fixed glass w-full h-screen left-0 top-0 z-0 opacity-100"></div>
                <div className="z-10 text-white text-lg">Učitavanje...</div>
            </div>
        );
    }

    if (!data) return null;

    return (
        <div className="fixed w-full h-screen left-0 top-0 z-10 flex flex-wrap justify-center content-center p-24 bg-cover" style={{ backgroundImage: `url(${background})` }}>
            <div className="fixed glass w-full h-screen left-0 top-0 z-0 opacity-100"></div>

            <div className="w-96 rounded-sm bg-white shadow-xl overflow-hidden z-10">
                <div className="text-left w-full text-sm p-6 overflow-auto">
                    {/* Header */}
                    <div className="text-center mb-5">
                        <img src={logo2} className="w-60 m-auto" alt="Logo" />
                        <h2 className="text-xl font-semibold mt-4 mb-1">Potvrda smjene</h2>
                        <p className="text-gray-500 text-sm">{data.date}</p>
                    </div>

                    {/* Error */}
                    {error && (
                        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
                            <span>{error}</span>
                            <span onClick={() => setError("")} className="absolute top-0 bottom-0 right-0 px-4 py-3 cursor-pointer">
                                <svg className="fill-current h-6 w-6 text-red-500" role="button" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                                    <path d="M14.348 14.849a1.2 1.2 0 0 1-1.697 0L10 11.819l-2.651 3.029a1.2 1.2 0 1 1-1.697-1.697l2.758-3.15-2.759-3.152a1.2 1.2 0 1 1 1.697-1.697L10 8.183l2.651-3.031a1.2 1.2 0 1 1 1.697 1.697l-2.758 3.152 2.758 3.15a1.2 1.2 0 0 1 0 1.698z" />
                                </svg>
                            </span>
                        </div>
                    )}

                    {/* Admin info */}
                    <div className="bg-gray-50 rounded-lg p-4 mb-4 space-y-2">
                        <div className="flex justify-between">
                            <span className="text-gray-500">Zaposlenik:</span>
                            <span className="font-semibold">{data.admin_name}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-500">Prijava:</span>
                            <span className="font-semibold">{data.login_time || "-"}</span>
                        </div>
                    </div>

                    {/* Shift info */}
                    {data.shift && !data.no_shift ? (
                        <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4 mb-4 space-y-2">
                            <div className="flex justify-between">
                                <span className="text-cyan-700">Smjena:</span>
                                <span className="font-semibold text-cyan-900">{data.shift.name}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-cyan-700">Radno vrijeme:</span>
                                <span className="font-semibold text-cyan-900">{data.shift.start_time} - {data.shift.end_time}</span>
                            </div>
                        </div>
                    ) : null}

                    {/* No shift warning */}
                    {data.no_shift && (
                        <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4 mb-4">
                            <p className="text-yellow-800 font-medium text-sm">
                                Smjena nije dodijeljena za danas. Kontaktirajte glavnog administratora.
                            </p>
                        </div>
                    )}

                    {/* Reject form */}
                    {showRejectForm && !data.no_shift && (
                        <div className="mb-4 space-y-2">
                            <label className="block text-sm font-medium text-gray-700">Razlog odbijanja:</label>
                            <textarea
                                value={rejectNote}
                                onChange={(e) => setRejectNote(e.target.value)}
                                rows={3}
                                className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-cyan-500 focus:border-cyan-500"
                                placeholder="Unesite razlog odbijanja smjene..."
                            />
                        </div>
                    )}
                </div>

                {/* Actions */}
                <div className="p-4 w-full space-y-3">
                    {data.no_shift ? (
                        /* Samo odjava kad nema smjene */
                        <button onClick={handleLogout} className="border border-gray-300 text-gray-700 text-lg px-4 py-3 rounded-sm w-full hover:bg-gray-50">
                            Odjava
                        </button>
                    ) : (
                        <>
                            {/* Potvrdi */}
                            <button onClick={handleConfirm} disabled={actionLoading} className="bg-primary text-white text-lg px-4 py-3 rounded-sm w-full focus:outline-none hover:bg-yellow-700">
                                {actionLoading ? "Potvrda..." : "Potvrdi smjenu"}
                            </button>

                            {/* Odbij */}
                            {showRejectForm ? (
                                <>
                                    <button onClick={handleReject} disabled={actionLoading} className="bg-red-600 text-white text-lg px-4 py-3 rounded-sm w-full hover:bg-red-700">
                                        {actionLoading ? "Slanje..." : "Pošalji odbijanje"}
                                    </button>
                                    <button onClick={() => { setShowRejectForm(false); setRejectNote(""); }} className="text-gray-600 text-sm px-4 py-2 w-full hover:text-gray-800">
                                        Odustani od odbijanja
                                    </button>
                                </>
                            ) : (
                                <button onClick={() => setShowRejectForm(true)} className="bg-gray-200 text-gray-800 text-lg px-4 py-3 rounded-sm w-full hover:bg-gray-300">
                                    Odbij smjenu
                                </button>
                            )}

                            {/* Odjava */}
                            <button onClick={handleLogout} className="border border-gray-300 text-gray-700 text-lg px-4 py-3 rounded-sm w-full hover:bg-gray-50">
                                Odjava
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
